// Rewards funnel + flow telemetry event
// definitions. See docs/rewards-launch-telemetry-plan.md §6.2 — these power the
// reached → bounced → connected → enrolled → earned conversion funnel and the
// per-flow success signals on the Better Stack "Rewards" dashboard.
//
// Each definition is a plain object with a `name` and a TypeScript phantom type
// for its properties, enforced via the EventDefinition generic — the same shape
// as every other file in this directory. Events are fired through the typed
// `onEvent('rewards_…', { … })` from `@/events`; the EventRegistry entries in
// `index.ts` are what make those calls type-check.

import type { EventDefinition } from './navigation';

export type RewardsEntryClickedProperties = {
  authenticated: boolean;
  source: 'sidebar' | 'portfolio';
};

// /rewards dashboard (or profile) first paint with the participant summary loaded.
export type RewardsPageViewedProperties = {
  view: 'dashboard' | 'profile';
  participantExists: boolean;
  xConnected: boolean;
};

// The rewards gate redirected a visitor away from /rewards.
export type RewardsGateBounceProperties = {
  /** `signed_out` = no session reached the gate; `logout` = intentional sign-out. */
  reason: 'signed_out' | 'logout';
};

// X (Turnkey OAuth) connect lifecycle from the join card.
export type RewardsXConnectStartedProperties = Record<string, never>;

export type RewardsXConnectSucceededProperties = Record<string, never>;

export type RewardsXConnectFailedProperties = {
  /** `error` = connectX resolved an error result; `null` = it threw/returned null; `timeout` = watchdog. */
  reason: 'error' | 'null' | 'timeout';
  /** Backend error code when `reason` is `error` — distinguishes already-linked from transient failures. */
  code?:
    | 'generic'
    | 'already_linked_elsewhere'
    | 'state_mismatch'
    | 'token_exchange_failed'
    | 'profile_fetch_failed';
};

// Participant (rewards enrollment) created.
export type RewardsParticipantCreatedProperties = {
  /** Whether a referral code rode the create request. */
  referred: boolean;
  /** True when created silently by the auto-enroll path (X already linked). */
  viaAutoEnroll: boolean;
};

// Morfi account claimed from the rewards profile card.
export type RewardsAccountCreatedProperties = Record<string, never>;

// Quest verification lifecycle (engagement quests verified on-demand vs the X API).
export type RewardsQuestStartedProperties = {
  questId: string;
  verifyMethod: number;
};

export type RewardsQuestVerifiedProperties = {
  questId: string;
};

// Quest verification failed — the on-demand X-API verify came back negative
// or unavailable. Completes the started → verified pair in the product
// analytics stream (Better Stack already had this via trackAction; the
// Statsig/Customer.io stream previously only saw successes).
export type RewardsQuestVerifyFailedProperties = {
  questId: string;
  reason: 'unverified' | 'unavailable' | 'private' | 'restricted';
};

// Post/clip content kit opened (the "Posts" / "Clips" sheets on /rewards).
export type RewardsContentKitOpenedProperties = {
  kind: 'post' | 'clip';
};

// A content-kit asset was taken — template post copied, clip downloaded, clip
// caption copied, or clip composed to X.
export type RewardsContentKitUsedProperties = {
  kind: 'post' | 'clip';
  action: 'copy' | 'download' | 'post_to_x';
};

// Daily streak claim.
export type RewardsStreakClaimedProperties = {
  /** Whether a tier-milestone bonus landed on top of the base daily points. */
  milestone: boolean;
};

// Milestone bonus claim.
export type RewardsMilestoneClaimedProperties = {
  milestoneId: string;
};

// Post / clip submission accepted (held for review).
export type RewardsContentSubmittedProperties = {
  kind: 'post' | 'clip';
};

// Auto-enroll spinner persisted past the watchdog threshold.
export type RewardsEnrollStuckProperties = {
  ms: number;
};

export const REWARDS_ENTRY_CLICKED: EventDefinition<RewardsEntryClickedProperties> = {
  name: 'rewards_entry_clicked',
};

export const REWARDS_PAGE_VIEWED: EventDefinition<RewardsPageViewedProperties> = {
  name: 'rewards_page_viewed',
};

export const REWARDS_GATE_BOUNCE: EventDefinition<RewardsGateBounceProperties> = {
  name: 'rewards_gate_bounce',
};

export const REWARDS_X_CONNECT_STARTED: EventDefinition<RewardsXConnectStartedProperties> = {
  name: 'rewards_x_connect_started',
};

export const REWARDS_X_CONNECT_SUCCEEDED: EventDefinition<RewardsXConnectSucceededProperties> = {
  name: 'rewards_x_connect_succeeded',
};

export type RewardsEmailCaptureOpenedProperties = Record<string, never>;

export type RewardsEmailVerifiedProperties = Record<string, never>;

export const REWARDS_EMAIL_CAPTURE_OPENED: EventDefinition<RewardsEmailCaptureOpenedProperties> = {
  name: 'rewards_email_capture_opened',
};

export const REWARDS_EMAIL_VERIFIED: EventDefinition<RewardsEmailVerifiedProperties> = {
  name: 'rewards_email_verified',
};

export const REWARDS_X_CONNECT_FAILED: EventDefinition<RewardsXConnectFailedProperties> = {
  name: 'rewards_x_connect_failed',
};

export const REWARDS_PARTICIPANT_CREATED: EventDefinition<RewardsParticipantCreatedProperties> = {
  name: 'rewards_participant_created',
};

export const REWARDS_ACCOUNT_CREATED: EventDefinition<RewardsAccountCreatedProperties> = {
  name: 'rewards_account_created',
};

export const REWARDS_QUEST_STARTED: EventDefinition<RewardsQuestStartedProperties> = {
  name: 'rewards_quest_started',
};

export const REWARDS_QUEST_VERIFIED: EventDefinition<RewardsQuestVerifiedProperties> = {
  name: 'rewards_quest_verified',
};

export const REWARDS_QUEST_VERIFY_FAILED: EventDefinition<RewardsQuestVerifyFailedProperties> = {
  name: 'rewards_quest_verify_failed',
};

export const REWARDS_CONTENT_KIT_OPENED: EventDefinition<RewardsContentKitOpenedProperties> = {
  name: 'rewards_content_kit_opened',
};

export const REWARDS_CONTENT_KIT_USED: EventDefinition<RewardsContentKitUsedProperties> = {
  name: 'rewards_content_kit_used',
};

export const REWARDS_STREAK_CLAIMED: EventDefinition<RewardsStreakClaimedProperties> = {
  name: 'rewards_streak_claimed',
};

export const REWARDS_MILESTONE_CLAIMED: EventDefinition<RewardsMilestoneClaimedProperties> = {
  name: 'rewards_milestone_claimed',
};

export const REWARDS_CONTENT_SUBMITTED: EventDefinition<RewardsContentSubmittedProperties> = {
  name: 'rewards_content_submitted',
};

export const REWARDS_ENROLL_STUCK: EventDefinition<RewardsEnrollStuckProperties> = {
  name: 'rewards_enroll_stuck',
};
